import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { "aria-label": "breadcrumb" }
const _hoisted_2 = {
  "data-test": "mobile-breadcrumb",
  class: "mobile-breadcrumb-wrapper"
}
const _hoisted_3 = ["rel"]

import {
  IonBreadcrumb,
  IonBreadcrumbs,
  IonPopover,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
} from "@ionic/vue";
import { chevronBackOutline, chevronForwardOutline } from "ionicons/icons";
import { onMounted, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

interface Props {
  tab: string;
  parentType?: string;
  grandparentType?: string;
  doc: string;
  parent?: string;
  parentTitle?: string;
  grandparent?: string;
  grandparentTitle?: string;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'Breadcrumbs',
  props: {
    tab: {},
    parentType: {},
    grandparentType: {},
    doc: { default: " " },
    parent: { default: "" },
    parentTitle: {},
    grandparent: {},
    grandparentTitle: {}
  },
  setup(__props: any) {

const { t } = useI18n<{}>({
  useScope: "global",
});
const router = useRouter();
const props = __props;

const maxCharsTwoBreadcrumbs = ref(121);
const maxCharsThreeBreadcrumbs = ref(73);
const popoverOpen = ref(false);
const collapsedBreadcrumbs = ref([]);
const event = ref(null);

const prev = computed(() => {
  return props.doc !== " "
    ? `${props.tab}/${parseType(props.parentType)}${
        props.parent ? `/${props.parent}` : ""
      }`
    : props.tab;
});
const tabName = computed(() => {
  switch (props.tab) {
    case "learn":
      return "pageTitles.learn";

    case "tools":
      return "pageTitles.tools";

    default:
      return "pageTitles.learn";
  }
});
const grandparentName = computed(() => {
  if (props.grandparentTitle) {
    return props.grandparentTitle;
  }
  return null;
});
const typeName = computed(() => {
  if (props.parentTitle) {
    return props.parentTitle;
  } else {
    switch (parseType(props.parentType)) {
      case "content-pack":
        return "Explore content";

      case "course":
        return "Courses";

      case "collection":
        return "Collections";

      case "quiz":
        return "quiz";

      case "resume":
        return "resume";

      default:
        return "Courses";
    }
  }
});
const isTitleFinal = computed(() => {
  const options = ["Explore content", "Courses", "Collections"];
  return options.includes(typeName.value);
});
const crumbLengthTotal = computed(() => {
  return (
    props.doc.length +
    tabName.value.length +
    (grandparentName.value?.length ?? 0) +
    typeName?.value.length
  );
});
const maxBreadcrumbsAmount = computed(() => {
  // Hide one breadcrumb (#2)
  if (crumbLengthTotal.value >= maxCharsTwoBreadcrumbs.value) return 2;
  // Hide two breadcrumbs (#2 and #3)
  else if (crumbLengthTotal.value >= maxCharsThreeBreadcrumbs.value) return 3;
  else return 4;
});
const numberOfAfterItemsToShow = computed(() => {
  return maxBreadcrumbsAmount.value == 2 ? 1 : 2;
});
const currentOrPreviousClass = computed(() => {
  return isTitleFinal.value ? "current-breadcrumb" : "previous-breadcrumb";
});
const isBreadcrumbHidden = computed(() => {
  return maxBreadcrumbsAmount.value < 4;
});

onMounted(() => {
  setTimeout(() => {
    // In Ionic 7 we should be able to set the alt text of the collapsed icon in the html
    // https://github.com/ionic-team/ionic-framework/pull/26615
    const breadcrumbEllipses = document.getElementsByClassName(
      "breadcrumb-collapsed"
    );
    if (breadcrumbEllipses?.length > 0) {
      breadcrumbEllipses[0].setAttribute(
        "aria-label",
        t("breadcrumb.showMoreBreadcrumbs")
      );

      // Stop screen reader adding "image button group".
      const collapsedRef = breadcrumbEllipses[0].collapsedRef;
      if (collapsedRef) {
        collapsedRef.setAttribute("aria-hidden", true);
      }
    }
  });
});
const parseType = (type) => {
  switch (type) {
    case "content_pack":
      return "content-pack";
    default:
      return type ? type : "";
  }
};
const presentPopover = (e) => {
  collapsedBreadcrumbs.value = e.detail.collapsedBreadcrumbs;
  event.value = e;
  popoverOpen.value = true;
  return false;
};
const goTo = (path) => {
  console.log("goTo called", path);
  popoverOpen.value = false;
  router.push({ path: path });
  return false;
};
const grandparentClick = () => {
  if (!isBreadcrumbHidden.value) {
    goTo(
      `/tabs/${props.tab}/${parseType(props.grandparentType)}/${
        props.grandparent || ""
      }`
    );
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("nav", _hoisted_1, [
    _createVNode(_unref(IonBreadcrumbs), {
      mode: "ios",
      class: "desktop-breadcrumb-wrapper",
      maxItems: maxBreadcrumbsAmount.value,
      itemsBeforeCollapse: 1,
      itemsAfterCollapse: numberOfAfterItemsToShow.value,
      onIonCollapsedClick: _cache[3] || (_cache[3] = ($event: any) => (presentPopover($event)))
    }, {
      default: _withCtx(() => [
        (__props.tab)
          ? (_openBlock(), _createBlock(_unref(IonBreadcrumb), {
              key: 0,
              "data-test": "tab-breadcrumb",
              class: "previous-breadcrumb",
              href: "#",
              rel: `/tabs/${__props.tab}`,
              onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (goTo(`/tabs/${__props.tab}`)), ["prevent"]))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t(tabName.value)) + " ", 1),
                _createVNode(_unref(IonIcon), {
                  slot: "separator",
                  icon: _unref(chevronForwardOutline),
                  "aria-hidden": "true"
                }, null, 8, ["icon"])
              ]),
              _: 1
            }, 8, ["rel"]))
          : _createCommentVNode("", true),
        (__props.grandparent || __props.grandparentType)
          ? (_openBlock(), _createBlock(_unref(IonBreadcrumb), {
              key: 1,
              "data-test": "type-breadcrumb",
              class: "previous-breadcrumb",
              href: "#",
              rel: `/tabs/${__props.tab}/${parseType(__props.grandparentType)}/${__props.grandparent || ''}`,
              onClick: _withModifiers(grandparentClick, ["prevent"]),
              separator: __props.doc !== ' ',
              active: __props.doc == ' '
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(__props.grandparentTitle) + " ", 1),
                _createVNode(_unref(IonIcon), {
                  slot: "separator",
                  icon: _unref(chevronForwardOutline),
                  "aria-hidden": "true"
                }, null, 8, ["icon"])
              ]),
              _: 1
            }, 8, ["rel", "separator", "active"]))
          : _createCommentVNode("", true),
        (__props.parentType)
          ? (_openBlock(), _createBlock(_unref(IonBreadcrumb), {
              key: 2,
              "data-test": "type-breadcrumb",
              class: _normalizeClass(currentOrPreviousClass.value),
              href: "#",
              rel: `/tabs/${__props.tab}/${parseType(__props.parentType)}/${__props.parent}`,
              onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (goTo(`/tabs/${__props.tab}/${parseType(__props.parentType)}/${__props.parent}`)), ["prevent"])),
              separator: __props.doc !== ' ',
              active: __props.doc == ' '
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(typeName.value) + " ", 1),
                _createVNode(_unref(IonIcon), {
                  slot: "separator",
                  icon: _unref(chevronForwardOutline),
                  "aria-hidden": "true"
                }, null, 8, ["icon"])
              ]),
              _: 1
            }, 8, ["class", "rel", "separator", "active"]))
          : _createCommentVNode("", true),
        (__props.doc)
          ? (_openBlock(), _createBlock(_unref(IonBreadcrumb), {
              key: 3,
              class: "current-breadcrumb",
              active: __props.doc !== ' '
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(__props.doc), 1)
              ]),
              _: 1
            }, 8, ["active"]))
          : _createCommentVNode("", true),
        _createVNode(_unref(IonPopover), {
          "is-open": popoverOpen.value,
          event: event.value,
          onDidDismiss: _cache[2] || (_cache[2] = ($event: any) => (popoverOpen.value = false))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(IonContent), null, {
              default: _withCtx(() => [
                _createVNode(_unref(IonList), null, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(collapsedBreadcrumbs.value, (breadcrumb, i) => {
                      return (_openBlock(), _createBlock(_unref(IonItem), {
                        key: i,
                        onClick: _withModifiers(($event: any) => (goTo(breadcrumb.rel)), ["prevent"]),
                        lines: 
                i === collapsedBreadcrumbs.value.length - 1 ? 'none' : undefined
              
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(IonLabel), null, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(breadcrumb.textContent), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_unref(IonIcon), {
                            icon: _unref(chevronForwardOutline),
                            "aria-hidden": "true"
                          }, null, 8, ["icon"])
                        ]),
                        _: 2
                      }, 1032, ["onClick", "lines"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["is-open", "event"])
      ]),
      _: 1
    }, 8, ["maxItems", "itemsAfterCollapse"]),
    _createElementVNode("p", _hoisted_2, [
      _createElementVNode("a", {
        class: "breadcrumb-chevron",
        href: "#",
        rel: `/tabs/${prev.value}`,
        onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (goTo(`/tabs/${prev.value}`)), ["prevent"])),
        "aria-hidden": "true"
      }, [
        _createVNode(_unref(IonIcon), {
          "aria-hidden": "true",
          icon: _unref(chevronBackOutline)
        }, null, 8, ["icon"])
      ], 8, _hoisted_3),
      (__props.doc !== ' ' && __props.parentType)
        ? (_openBlock(), _createBlock(_unref(IonBreadcrumb), {
            key: 0,
            class: "mobile-breadcrumb previous-breadcrumb truncate",
            href: "#",
            rel: `/tabs/${prev.value}`,
            onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (goTo(`/tabs/${prev.value}`)), ["prevent"]))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(typeName.value) + " ", 1),
              _createVNode(_unref(IonIcon), {
                slot: "separator",
                icon: _unref(chevronForwardOutline),
                "aria-hidden": "true"
              }, null, 8, ["icon"])
            ]),
            _: 1
          }, 8, ["rel"]))
        : (_openBlock(), _createBlock(_unref(IonBreadcrumb), {
            key: 1,
            class: "mobile-breadcrumb truncate current-breadcrumb",
            href: "#",
            rel: `/tabs/${__props.tab}`,
            onClick: _cache[6] || (_cache[6] = _withModifiers(($event: any) => (goTo(`/tabs/${__props.tab}`)), ["prevent"]))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t(tabName.value)), 1)
            ]),
            _: 1
          }, 8, ["rel"]))
    ])
  ]))
}
}

})